//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Connect, Edit, Add } from '@carbon/icons-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import OverrideConfigModal from './OverrideConfigModal';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import IntegrationPreferencesService from '../../utils/IntegrationPreferencesService';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useUserAuth } from '../../UserAuth';
import { useConfig } from '../../Config';
import {
  AccessibleIntegrationOption,
  announceDefaultIntegrationChanged,
  DEFAULT_INTEGRATION_CHANGED_EVENT,
  IntegrationPreferences,
  isOwnDefaultChange,
} from '../../types/integrationPreferences';

const EVENT_SOURCE = 'member-access';

/**
 * A path is identified by its group, not by the integration name: two groups can grant the
 * same integration with different override policies, so they are distinct rows.
 */
const optionKey = (o: AccessibleIntegrationOption): string =>
  `${o.groupId}::${o.integrationName}`;

/**
 * "Integrations Member Access" — every AI integration the signed-in user can reach, which
 * group grants each one, and whether they are using their own credentials or the group's.
 *
 * Replaces two disconnected surfaces: a preferences list that could set a default but not
 * edit credentials, and a profile-menu modal that edited credentials but only ever for the
 * user's active path. A user with access through several groups could not reach the others
 * at all.
 *
 * Layout deliberately mirrors GroupsPage: grey canvas, white card, counted header, and the
 * same table treatment, so the two group-related pages read as one feature.
 */
const MemberAccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { teamspaceId } = useTeamspaceContext();
  const { user } = useUserAuth();
  const { services } = useConfig();
  const tenantBaseUrl = services?.tenant?.url ?? '';
  // Depend on the token itself rather than the user object: auth providers commonly return
  // a fresh object each render, which would make `load` a new function every time and send
  // the fetch effect into a loop.
  const accessToken = user?.access_token;
  // One stable function per token. The preferences and group services are singletons that
  // rebuild themselves whenever they are handed a different function, so an inline arrow
  // at each call site would discard and recreate them on every call.
  const getUserContext = useCallback(
    () => ({ access_token: accessToken }),
    [accessToken],
  );
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const [preferences, setPreferences] = useState<IntegrationPreferences | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [overrideTarget, setOverrideTarget] =
    useState<AccessibleIntegrationOption | null>(null);

  // `silent` refreshes in place: a background refresh must not swap the table for the
  // loading state, which would flash the page every time the window regains focus.
  const load = useCallback(
    async (silent = false) => {
      if (!teamspaceId) {
        // No teamspace selected: nothing to load. Without this the page kept its initial
        // loading state forever, which is the state a user with no teamspace sees.
        setLoading(false);
        return;
      }
      if (!tenantBaseUrl) {
        // Configuration not resolved yet; the effect runs again when it is.
        return;
      }
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const service = IntegrationPreferencesService.getInstance(
          tenantBaseUrl,
          getUserContext,
        );
        setPreferences(await service.getPreferences(teamspaceId));
      } catch (e: any) {
        setError(e?.message || t('userGroups.preferencesLoadFailed'));
      } finally {
        setLoading(false);
      }
    },
    [teamspaceId, tenantBaseUrl, getUserContext, t],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Pick up changes made elsewhere: the Loop Genie default prompt fires the event, and
  // another tab can only be noticed when this one regains focus.
  useEffect(() => {
    const refresh = (event: Event) => {
      if (isOwnDefaultChange(event, EVENT_SOURCE)) return;
      load(true);
    };
    window.addEventListener(DEFAULT_INTEGRATION_CHANGED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(DEFAULT_INTEGRATION_CHANGED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [load]);

  const options = preferences?.options ?? [];

  const handleSetDefault = async (option: AccessibleIntegrationOption) => {
    setSavingKey(optionKey(option));
    setError(null);
    try {
      const service = IntegrationPreferencesService.getInstance(
        tenantBaseUrl,
        getUserContext,
      );
      await service.setDefaultIntegration(teamspaceId!, {
        integrationName: option.integrationName,
        selectedGroupId: option.groupId,
      });
      // Re-read rather than patching state: the server owns which path is valid.
      await load();
      // Tell other open views (Loop Genie's gate) so they do not keep stale state. This
      // page has just refreshed itself, so it ignores its own announcement.
      announceDefaultIntegrationChanged(EVENT_SOURCE);
    } catch (e: any) {
      setError(e?.message || t('userGroups.preferencesSaveFailed'));
    } finally {
      setSavingKey(null);
    }
  };

  const renderCredentials = (option: AccessibleIntegrationOption) => {
    const key = optionKey(option);
    if (option.isEnabled === false) {
      return (
        <span
          className="italic text-gray-400 dark:text-gray-500"
          data-testid={`credentials-unavailable-${key}`}
        >
          {t('userGroups.integrationUnavailable', 'Integration disabled')}
        </span>
      );
    }
    if (!option.allowUserOverride) {
      // Checked BEFORE "has an override": a group that forbids overrides never applies one,
      // even if one was saved before the policy changed, so reporting it as Custom would
      // claim credentials that are not in use. Distinguished from "not set" because this
      // state is permanent rather than something the user can act on.
      return (
        <span
          className="italic text-gray-400 dark:text-gray-500"
          data-testid={`credentials-locked-${key}`}
        >
          {t('userGroups.credentialsLocked', 'Not permitted')}
        </span>
      );
    }
    if (option.hasUserOverride) {
      return (
        <span className="font-medium" data-testid={`credentials-custom-${key}`}>
          {t('userGroups.customCredentials', 'Custom')}
        </span>
      );
    }
    return (
      <span
        className="text-gray-500 dark:text-gray-400"
        data-testid={`credentials-group-${key}`}
      >
        {t('userGroups.groupCredentials', 'Group default')}
      </span>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div
          className="flex flex-col gap-2"
          data-testid="member-access-loading"
        >
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      );
    }

    if (!teamspaceId) {
      return (
        <div
          data-testid="member-access-no-teamspace"
          className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400"
        >
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            {t(
              'userGroups.noTeamspaceSelected',
              'Select a teamspace to see your AI integration access.',
            )}
          </p>
        </div>
      );
    }

    if (options.length === 0) {
      return (
        <div
          data-testid="member-access-empty"
          className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400"
        >
          <Connect
            size={48}
            className="mb-4 text-gray-400 dark:text-gray-500"
          />
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            {t('userGroups.noIntegrationAccess')}
          </p>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader className="bg-gray-100 dark:bg-[#393939]">
          <TableRow className="border-b border-gray-300 dark:border-gray-600">
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.integrationColumn', 'Integration')}
            </TableHead>
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.credentialType', 'Credential Type')}
            </TableHead>
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.accessVia', 'Access via')}
            </TableHead>
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.myCredentials', 'My Credentials')}
            </TableHead>
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.default', 'Default')}
            </TableHead>
            <TableHead className="text-right font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.actions', 'Actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {options.map((option) => {
            const key = optionKey(option);
            return (
              <TableRow
                key={key}
                data-testid={`member-access-row-${key}`}
                className={option.isEnabled === false ? 'opacity-60' : ''}
              >
                <TableCell className="font-medium">
                  <div className="truncate font-semibold">
                    {option.integrationName}
                  </div>
                </TableCell>
                <TableCell className="text-gray-500 dark:text-gray-400">
                  {option.credentialType || 'API Key'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400">🔌</span>
                    <span className="font-medium">{option.groupName}</span>
                  </div>
                </TableCell>
                <TableCell>{renderCredentials(option)}</TableCell>
                <TableCell>
                  {/* One radio group across the table: picking a default is a choice between
                      rows, not a per-row action, so a radio says that where a button did not.
                      A disabled integration cannot be chosen. */}
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="member-access-default"
                      className="h-4 w-4 text-[#3c91ff] focus:ring-[#3c91ff] disabled:cursor-not-allowed disabled:opacity-50"
                      // Coerced to a real boolean: `checked={undefined}` makes the input
                      // uncontrolled, so a click would stick visually even when the save
                      // was rejected, and React would never correct it.
                      checked={option.isCurrentDefault === true}
                      disabled={option.isEnabled === false || savingKey === key}
                      onChange={() => handleSetDefault(option)}
                      data-testid={`default-radio-${key}`}
                      aria-label={`${t('userGroups.setAsDefault', 'Set as default')} ${
                        option.integrationName
                      } ${option.groupName}`}
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {savingKey === key
                        ? t('userGroups.saving')
                        : option.isCurrentDefault
                          ? t('userGroups.currentDefault', 'Current default')
                          : ''}
                    </span>
                  </label>
                </TableCell>
                <TableCell className="text-right">
                  {/* No remove action: the UI has never exposed override deletion, so the
                      only operations offered are adding one and editing it. */}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={
                        !option.allowUserOverride || option.isEnabled === false
                      }
                      onClick={() => setOverrideTarget(option)}
                      data-testid={`override-action-${key}`}
                      className="flex items-center gap-2"
                      aria-label={`${
                        option.hasUserOverride
                          ? t('userGroups.editOverride', 'Edit override')
                          : t('userGroups.addOverride', 'Add override')
                      } ${option.integrationName}`}
                    >
                      {option.hasUserOverride ? (
                        <Edit size={16} />
                      ) : (
                        <Add size={16} />
                      )}
                      {option.hasUserOverride
                        ? t('userGroups.editOverride', 'Edit override')
                        : t('userGroups.addOverride', 'Add override')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <div
      data-testid="member-access-page"
      className="min-h-screen bg-[#F4F4F4] p-6 dark:bg-[#161616]"
    >
      <div className="container mx-auto">
        {/* White Card Container */}
        <div className="rounded-lg bg-white p-6 dark:bg-[#262626]">
          {/* Header with title and count */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-medium text-[#4a4a56] dark:text-[#c8c8dd]">
                {t(
                  'userGroups.memberAccessTitle',
                  'Integrations Member Access',
                )}
              </h2>
              {!loading && (
                <span
                  className="rounded-full bg-[#3c91ff] px-2 py-0.5 text-xs font-semibold text-white"
                  data-testid="member-access-count"
                >
                  {options.length}
                </span>
              )}
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div
              data-testid="member-access-error"
              className="mb-4 rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {error}
            </div>
          )}

          {renderBody()}
        </div>
      </div>

      {overrideTarget && teamspaceId && user && (
        <OverrideConfigModal
          isOpen={!!overrideTarget}
          teamspaceId={teamspaceId}
          tenantBaseUrl={tenantBaseUrl}
          getUserContext={getUserContext}
          integrationName={overrideTarget.integrationName}
          // Overrides are per group: a user can reach this integration through several, and
          // the override being edited belongs to the group of the row that was clicked.
          groupId={overrideTarget.groupId}
          onClose={() => setOverrideTarget(null)}
          onSuccess={() => {
            setOverrideTarget(null);
            showSnackbar(
              t(
                'userGroups.overrideSaved',
                'Integration override saved successfully',
              ),
              'success',
            );
            // Refresh so the My Credentials column reflects the new override.
            load();
          }}
        />
      )}
      <CustomSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={closeSnackbar}
      />
    </div>
  );
};

export default MemberAccessPage;
